// Directives
import directives from '../directives/index.js'

// Libraries
import Vue from 'vue'

// Services
import router from '../router'
import store from '../store'

// Utilities
import analytics from '../utility/analyticsUtil'
import isEmpty from '../utility/isEmpty'
import logger from '../utility/loggerUtil'
import sessionRecord from '../utility/sessionRecordUtil'
import session from '../utility/sessionUtil.js'
import { getDefaultState } from '../utility/applicationUtil'
import { KeyConstants } from '../constants'

export default class StartupService {
  static async init () {
    await this.initServiceWorker()
    this.initDeviceInfo()
    this.initDirectives()
    this.initGlobalRouteGuards()
    this.initSiteData()

    await this.getConfigSettings()
    await StartupService.initUserDataCollection()
  }

  static async initUserDataCollection () {
    logger.debug('Initializing user data collection services...')

    await StartupService.initAnalytics()
  }

  static async initAnalytics () {
    await analytics.init()

    analytics.saveInitUserProperties()
    sessionRecord.saveInitUserProperties()
  }

  static initDirectives () {
    // Formats a currency input to 2 decimal precision
    // TODO: Fix jerky input when typing in middle or before $
    Vue.directive('currency', directives.CurrencyDirective)

    // Auto focuses the element on page load
    Vue.directive('focus', directives.FocusDirective)

    // Formats input to currency and prevents non-numeric inputs
    Vue.directive('inputCurrency', directives.InputCurrencyDirective)

    Vue.directive('masked', directives.MaskedDirective)

    // Prevents non-numeric inputs
    Vue.directive('numeric', directives.NumericDirective)

    Vue.directive('uppercase', directives.UppercaseDirective)
  }

  static initDeviceInfo () {
    store.commit('display/setDeviceBrowserInfo')
  }

  static async getConfigSettings () {
    try {
      const defaultState = getDefaultState()
      const loanProductType = store.getters['applicationCustomer/loanProduct']
      await Promise.all([
        store.dispatch('session/getConfigSettings'),
        store.dispatch('applicationSession/getProductConfiguration', { state: defaultState, productType: loanProductType })
      ])
    } catch (e) {
      logger.error('Error attempting to get config settings')
      logger.debug(e)
    }
  }

  /**
   * Initializes route guards to prevent or redirect navigation depending on defined conditions.
   */
  static initGlobalRouteGuards () {
    const navigationAllowed = (to, from) => { // Checks if navigation is allowed from one specific route to another
      if (!isEmpty(from.meta) && !isEmpty(from.meta.preventNavigation) && from.meta.preventNavigation.includes(to.name)) {
        return false
      }

      return true
    }

    router.beforeEach((to, from, next) => {
      if (navigationAllowed(to, from) === false) { // Create more functions and add them here to alter navigation with different conditions
        next(false)
        return
      }

      next()
    })
  }

  /**
   * Registers a new service worker, required for PWA config.
   */
  static async initServiceWorker () {
    if (!('serviceWorker' in navigator)) {
      logger.debug('ServiceWorker not supported')
      return
    }

    try {
      const registration = await navigator.serviceWorker.register('/serviceWorker.js')

      logger.debug('ServiceWorker registration successful with scope: ' + registration.scope)

      // Installed PWAs can stay open for days, so check for a new deploy whenever the app comes back to the foreground
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(e => logger.debug(e))
        }
      })
    } catch (e) {
      logger.error('Error registering service worker')
      logger.debug(e)
    }
  }

  static initSiteData () {
    try {
      const qrScannedStoreNumber = session.getCookie(KeyConstants.cookies.qrScanStoreNumberRedirect)

      if (!isEmpty(qrScannedStoreNumber)) {
        store.commit('applicationRetailStore/setQrScannedStoreNumber', { qrScannedStoreNumber })
      }
    } catch (error) {
      logger.error('Error checking for QR scanned store number')
      logger.debug(error)
    }
  }
}
